import React from 'react';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { useForm } from 'react-hook-form';
import { InternalRefetchQueryDescriptor, useMutation } from '@apollo/client';

import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';
import ActionButtons from '../../common/ActionButtons';
import EntityTerminationBlockerEditForm from './EntityTerminationBlockerEditForm';
import { IEntityTerminationActionItem } from '../types';
import {
  CREATE_BLOCKER_QUEUE_ACTION_ITEM,
  UPDATE_BLOCKER_QUEUE_ACTION_ITEM,
} from '../../../utils/statApi/EntityTerminationApi';

interface IBlockerEditPanelProps {
  closePanel: () => void;
  isNewBlocker: boolean;
  selectedBlocker?: IEntityTerminationActionItem;
  companyCode: string;
  refetchQueries: InternalRefetchQueryDescriptor[];
  existingBlockers?: IEntityTerminationActionItem[];
}

const EntityTerminationBlockerEditPanel = (props: IBlockerEditPanelProps): JSX.Element => {
  const {
    closePanel,
    isNewBlocker,
    selectedBlocker,
    companyCode,
    refetchQueries,
    existingBlockers,
  } = props;
  const blockerForm = useForm();
  const { handleSubmit } = blockerForm;

  const title = isNewBlocker ? 'Create New' : 'Edit';

  const [createBlocker, { loading: createLoading }] = useMutation(
    CREATE_BLOCKER_QUEUE_ACTION_ITEM,
    {
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        closePanel();
      },
    },
  );

  const [updateBlocker, { loading: updateLoading }] = useMutation(
    UPDATE_BLOCKER_QUEUE_ACTION_ITEM,
    {
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        closePanel();
      },
    },
  );

  const handleSaveClick = (formData: IEntityTerminationActionItem): void => {
    const ownerInput = formData.owner
      ? {
          id: formData.owner.id,
          graphId: formData.owner.graphId,
          name: formData.owner.name,
          firstName: formData.owner.firstName,
          lastName: formData.owner.lastName,
          email: formData.owner.email,
          alias: formData.owner.alias,
        }
      : null;

    const legoCategoryInput = formData.legoCategory
      ? {
          id: formData.legoCategory.id,
          fieldName: formData.legoCategory.fieldName,
        }
      : null;

    const blockerVariables = {
      companyCode,
      blockerQueueActionItemDto: {
        id: selectedBlocker?.id ?? null,
        companyCode,
        legoCategory: legoCategoryInput,
        description: formData.description,
        owner: ownerInput,
        status: formData.status,
        dateStarted: formData.dateStarted,
        dateCompleted: formData.dateCompleted || null,
        isPrimaryBlocker: formData.isPrimaryBlocker ?? false,
      },
    };

    if (isNewBlocker) {
      createBlocker({ variables: { ...blockerVariables } });
    } else {
      updateBlocker({ variables: { ...blockerVariables } });
    }
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText={`${title} Blocker`}
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      closeButtonAriaLabel="Close Blocker Panel"
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          mutationLoading={createLoading || updateLoading}
          closePanel={closePanel}
          saveLabel="Save Blocker"
          saveTitle="Save Blocker"
          cancelLabel="Cancel"
          cancelTitle="Cancel"
          handleSubmit={handleSubmit(handleSaveClick)}
        />
      )}
    >
      <form>
        <EntityTerminationBlockerEditForm
          form={blockerForm}
          selectedBlocker={selectedBlocker}
          existingBlockers={existingBlockers}
        />
      </form>
    </CoherencePanel>
  );
};

export default EntityTerminationBlockerEditPanel;
